﻿import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import CustomInfoMessage from "@/components/desktop/list/CustomInfoMessage";

import useFilterStore from "@/stores/useFilterStore";
import useListController from "@/hooks/list/useListController";
import { useDistributionLists, useDeleteDistributionList, useUpdateDistributionList } from "@/hooks/maintainers/distribution-lists/useDistributionLists";

import useDistributionListColumns from "./hooks/useDistributionListColumns";
import DistributionListMembers from "./DistributionListMembers";
import DistributionListForm from "./Form/DistributionListForm";

import { base_filter, base_table } from "./distribution-lists.constants";

const DistributionLists = () => {
	const navigate = useNavigate();
	const { option, element_id } = useParams();

	const { filter } = useFilterStore();

	const { estate_id, estate_parent_id, search } = filter || {};

	const {
		isFilterOpen,
		filtersState,
		tableState,
		appliedFilterParams,
		reactTableSort,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
	} = useListController({ baseFilter: base_filter, baseTable: base_table });

	const filters = useMemo(
		() => ({
			...appliedFilterParams,
			estate_id,
			estate_parent_id,
			search,
			...tableState,
		}),
		[appliedFilterParams, estate_id, estate_parent_id, search, tableState],
	);

	const { data, isLoading, isError, error } = useDistributionLists(filters);

	const { mutate: deleteList, isLoading: isLoadingDelete } = useDeleteDistributionList();
	const { mutate: updateList, isLoading: isLoadingUpdate } = useUpdateDistributionList();

	const extractErrorMessage = (err, fallback) => {
		const apiMessages = err?.response?.data?.messages;
		if (Array.isArray(apiMessages) && apiMessages.length > 0) return apiMessages.join(" \n");
		if (err?.response?.data?.message) return err.response.data.message;
		if (err?.message) return err.message;
		return fallback;
	};

	const [showDelete, setShowDelete] = useState(false);
	const [selected, setSelected] = useState(null);
	const [showForm, setShowForm] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [infoMessages, setInfoMessages] = useState([]);
	const [messageType, setMessageType] = useState("info");

	useEffect(() => {
		if (isError) {
			const message = extractErrorMessage(error, "Error al cargar listas de difusión");
			setMessageType("error");
			setInfoMessages([message]);
		} else {
			setInfoMessages([]);
		}
	}, [isError, error]);

	const handleDelete = () => {
		setShowDelete(false);

		deleteList(selected, {
			onSuccess: () => {
				toast.success("Lista de difusión eliminada correctamente");
			},
			onError: (err) => {
				setMessageType("error");
				setInfoMessages([extractErrorMessage(err, "Error al eliminar lista de difusión")]);
			},
		});
	};

	const handleToggleActive = (listId, newIsActive) => {
		const currentList = data?.data?.find((list) => list.id === listId);
		if (!currentList) {
			toast.error("No se pudo encontrar la lista");
			return;
		}

		const form = {
			name: currentList.name,
			description: currentList.description || "",
			is_active: newIsActive,
			notification_type: currentList.notification_type || "email",
			estate_parent_ids: currentList.estates?.map((e) => e.estate_parent_id) || [],
			members: currentList.members || [],
		};

		updateList(
			{ listId, form, filters },
			{
				onSuccess: () => {
					toast.success(`Lista ${newIsActive ? "activada" : "desactivada"} correctamente`);
				},
				onError: (err) => {
					setMessageType("error");
					setInfoMessages([extractErrorMessage(err, `Error al ${newIsActive ? "activar" : "desactivar"} lista de difusión`)]);
				},
			},
		);
	};

	const handleCreate = () => {
		navigate("/configuracion/listas_difusion/crear");
	};

	const handleEdit = (slug) => {
		navigate(`/configuracion/listas_difusion/editar/${slug}`);
	};

	const handleCloseForm = () => {
		setShowForm(false);
		setEditingId(null);
		navigate("/configuracion/listas_difusion");
	};

	const renderExpandedContent = useCallback((row) => {
		return <DistributionListMembers members={row?.original?.members || []} />;
	}, []);

	useEffect(() => {
		if (option === "crear") {
			setEditingId(null);
			setShowForm(true);
			return;
		}

		if (option === "editar" && element_id) {
			setEditingId(element_id);
			setShowForm(true);
			return;
		}

		setShowForm(false);
		setEditingId(null);
	}, [option, element_id]);

	const columns = useDistributionListColumns(tableState, {
		onEdit: (id) => handleEdit(id),
		onDelete: (id) => {
			setSelected(id);
			setShowDelete(true);
		},
		onToggleActive: handleToggleActive,
	});

	return (
		<ListScaffold
			titleBreadcrumbs={[
				{ label: "Dashboard", onClick: () => navigate("/") },
				{ label: "Configuración", onClick: () => navigate("/configuracion") },
				{ label: "Listas de Difusión", onClick: () => navigate("/configuracion/listas_difusion") },
			]}
			subtitle="Gestiona las listas de difusión para enviar notificaciones y comunicaciones"
			showCreate
			createLabel="Nueva Lista de Difusión"
			onCreate={handleCreate}
			actionButtons={[]}
			filterTitle="Filtrar listas de difusión"
			isFilterOpen={isFilterOpen}
			filters={filtersState}
			onFilterChange={handleFilterChange}
			onApplyFilters={handleApplyFilters}
			onClearFilters={handleClearAllFilters}
			onClearOneFilter={handleClearOneFilter}
			onOpenFilter={openFilter}
			onCloseFilter={closeFilter}
			isLoading={isLoading || isLoadingDelete || isLoadingUpdate}
			data={data?.data || []}
			totalItems={data?.totalRecords || 0}
			totalPages={data?.totalPages || 0}
			toItem={Math.min(tableState.page * tableState.per_page, data?.totalRecords || 0)}
			fromItem={data?.totalRecords ? (tableState.page - 1) * tableState.per_page + 1 : 0}
			pageIndex={tableState.page}
			columns={columns}
			onPageChange={handlePageChange}
			pageSize={tableState.per_page}
			onPageSizeChange={handlePageSizeChange}
			sortBy={reactTableSort}
			onSortByChange={handleSortByChange}
			pageSizeOptions={[20, 50, 100]}
			initialPageSize={tableState.per_page}
			initialSortBy={reactTableSort}
			compactHeaders={true}
			useExpandableRows
			renderExpandedContent={renderExpandedContent}
			topContent={
				infoMessages.length > 0 && (
					<CustomInfoMessage messages={infoMessages} type={messageType} autoCloseDuration={30000} onClose={() => setInfoMessages([])} />
				)
			}
		>
			<DistributionListForm isOpen={showForm} onClose={handleCloseForm} editingId={editingId} filters={filters} />

			<BasicConfirm
				showConfirm={showDelete}
				setShowConfirm={setShowDelete}
				handleAction={handleDelete}
				showValidation={true}
				textValidation={"eliminar lista de difusion"}
			/>
		</ListScaffold>
	);
};

export default DistributionLists;
